// Общие помощники функции «таблицы, рисунки, листинги»: поиск узлов, единицы длины, выбор и чтение файлов.
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';

/** Ширина поля набора, см (A4, поля 25/15 мм) - как TEXT_W в writer. */
export const TEXT_W_CM = 17;
/** Наибольшая высота рисунка, см (writer: MAX_IMG_H_CM) - выше writer уменьшает. */
export const MAX_H_CM = 22;
/** CSS-пиксели на сантиметр (96 dpi). */
export const PX_PER_CM = 96 / 2.54;
/** Ширина поля набора в CSS-пикселях: ширины граф таблиц нормируются к ней (colwidth в пикселях). */
export const TEXT_W_PX = Math.round(TEXT_W_CM * PX_PER_CM);

/** Ширина рисунка ('15cm' | '120mm' | '80%' | null) -> сантиметры (null - натуральный размер). */
export function widthToCm(w: string | null | undefined): number | null {
  if (!w) return null;
  const m = String(w).replace(',', '.').match(/^\s*([\d.]+)\s*(cm|mm|%)\s*$/);
  if (!m) return null;
  const v = Number(m[1]);
  return m[2] === 'cm' ? v : m[2] === 'mm' ? v / 10 : (TEXT_W_CM * v) / 100;
}

/** Сантиметры -> атрибут ширины '12.5cm'. */
export function cmToWidth(cm: number): string {
  return `${Math.round(cm * 10) / 10}cm`;
}

/** 12.5 -> «12,5 см». */
export function fmtCm(cm: number): string {
  return `${(Math.round(cm * 10) / 10).toLocaleString('ru-RU')} см`;
}

/** Натуральный размер изображения в см (как writer: пиксели / dpi, dpi вне 50–1200 = 96). */
export function naturalCm(px: number, dpi?: number | null): number {
  const d = dpi && dpi >= 50 && dpi <= 1200 ? dpi : 96;
  return (px / d) * 2.54;
}

/** Итоговая ширина рисунка в отчёте (см) с ограничениями writer: не шире поля, не выше 22 см. */
export function effectiveCm(width: string | null, wpx: number, hpx: number, dpi?: number | null): number {
  let w = widthToCm(width) ?? naturalCm(wpx, dpi);
  if (w > TEXT_W_CM) w = TEXT_W_CM;
  if (wpx > 0 && hpx > 0) {
    const h = (w * hpx) / wpx;
    if (h > MAX_H_CM) w = (MAX_H_CM * wpx) / hpx;
  }
  return w;
}

/** Позиция и узел ближайшего предка заданного типа от начала выделения. */
export function ancestor(editor: Editor, type: string): { pos: number; node: PMNode; depth: number } | null {
  const $f = editor.state.selection.$from;
  for (let d = $f.depth; d > 0; d--) {
    const n = $f.node(d);
    if (n.type.name === type) return { pos: $f.before(d), node: n, depth: d };
  }
  const sel = editor.state.selection as any;
  if (sel.node && sel.node.type.name === type) return { pos: sel.from, node: sel.node, depth: $f.depth + 1 };
  return null;
}

/** Найти узел по id: {pos, node} или null. */
export function findById(doc: PMNode, id: string): { pos: number; node: PMNode } | null {
  let out: { pos: number; node: PMNode } | null = null;
  doc.descendants((n, pos) => {
    if (out) return false;
    if (n.attrs?.id === id) { out = { pos, node: n }; return false; }
    return true;
  });
  return out;
}

/** Выбрать файлы системным диалогом (по действию пользователя). */
export function pickFiles(accept: string, multiple = true): Promise<File[]> {
  return new Promise((resolve) => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = accept;
    inp.multiple = multiple;
    inp.style.display = 'none';
    inp.onchange = () => { resolve(Array.from(inp.files || [])); inp.remove(); };
    document.body.appendChild(inp);
    inp.click();
  });
}

/** Текст файла: UTF-8 (с BOM или без); если в UTF-8 не читается - Windows-1251 (старые исходники). */
export async function readTextFile(f: Blob): Promise<string> {
  const buf = await f.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('windows-1251').decode(buf);
  }
}

/** Имя файла -> предложенное название рисунка (или '' для безликих имён: скриншоты, IMG_1234, image.png). */
export function captionFromFilename(name: string): string {
  let s = (name || '').replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s || s.length < 3) return '';
  if (/^(image|img|pic|photo|screenshot|screen|clipboard|вставка|изображение|рисунок|снимок|скриншот|dsc|dcim|pxl|mvimg|scan|безымянный|untitled|file|blob)\b/i.test(s)) return '';
  if (/^[\d\s\-.()]+$/.test(s) || /^[a-z]{0,4}[\d\s\-.]{5,}$/i.test(s) || /^[0-9a-f-]{16,}$/i.test(s)) return '';
  if (!/[а-яё]/i.test(s) && !/\s/.test(s) && /[A-Z].*[a-z]|[a-z].*[A-Z]|\d/.test(s)) return '';   // «IMG2034», «myScreen1»
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s.replace(/\.$/, '');
}

/** Склонение: 1 рисунок, 2 рисунка, 5 рисунков. */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100; const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
